import { useEffect, useState } from 'react';

import { useCurrentUser } from '@klareospace/pharos-lib-front-utils';
import type { Task } from '@klareospace/pharos-lib-types';

import {
  NONE_CARRIER_VALUE,
  NONE_DRIVER_VALUE,
  resolveCanSave,
  resolveIsDirty,
  TASK_ASSIGNMENT_MODE,
  type DraftAssignment,
  type TaskAssignmentMode,
} from '../utils/task-assignment';
import { useAssignTask } from './use-assign-task';
import { useReleaseTask } from './use-release-task';
import { useSelfAssignTask } from './use-self-assign-task';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseTaskAssignmentDraftReturn {
  /** Whether the current draft may be persisted (see `resolveCanSave`). */
  canSave: boolean;
  /** Draft carrier id, sentinel-encoded — `NONE_CARRIER_VALUE` when unset/unassigned. */
  draftCarrierId: string;
  /** Draft driver id, sentinel-encoded — `NONE_DRIVER_VALUE` when unset. */
  draftDriverId: string;
  /** Reverts the draft to the persisted (task) values. */
  handleCancel: () => void;
  /** Sets the draft carrier and resets the draft driver (a carrier change invalidates the driver choice). */
  handleCarrierChange: (carrierId: string) => void;
  /** Sets the draft driver. */
  handleDriverChange: (driverId: string) => void;
  /** Persists the draft via the assign, self-assign, or release mutation per `mode`; no-op in `readOnly`. */
  handleSave: () => void;
  /** Whether the draft differs from the persisted values (see `resolveIsDirty`) — gates Save/Cancel visibility. */
  isDirty: boolean;
  /** True while the assign, self-assign, or release mutation is in flight. */
  isPending: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Owns the Carrier & Driver form's draft lifecycle: sync, dirty/canSave tracking, revert, and save. */
export function useTaskAssignmentDraft(
  task: Task,
  mode: TaskAssignmentMode,
  onTaskUpdated: (task: Task) => void,
): UseTaskAssignmentDraftReturn {
  const { user } = useCurrentUser();

  const persisted: DraftAssignment = {
    carrierId: task.carrier?.id ?? NONE_CARRIER_VALUE,
    driverId: task.driver?.id ?? NONE_DRIVER_VALUE,
  };

  const [draftCarrierId, setDraftCarrierId] = useState(persisted.carrierId);
  const [draftDriverId, setDraftDriverId] = useState(persisted.driverId);

  useEffect(() => {
    setDraftCarrierId(persisted.carrierId);
    setDraftDriverId(persisted.driverId);
  }, [persisted.carrierId, persisted.driverId]);

  const assignMutation = useAssignTask(task.order.id);
  const selfAssignMutation = useSelfAssignTask(task.order.id);
  const releaseMutation = useReleaseTask(task.order.id);
  const isPending = assignMutation.isPending || selfAssignMutation.isPending || releaseMutation.isPending;

  function handleCarrierChange(carrierId: string): void {
    setDraftCarrierId(carrierId);
    setDraftDriverId(NONE_DRIVER_VALUE);
  }

  function handleCancel(): void {
    setDraftCarrierId(persisted.carrierId);
    setDraftDriverId(persisted.driverId);
  }

  function handleSave(): void {
    if (mode === TASK_ASSIGNMENT_MODE.ASSIGN) {
      assignMutation.mutate(
        {
          data: {
            carrierId: draftCarrierId === NONE_CARRIER_VALUE ? null : draftCarrierId,
            driverId: draftDriverId === NONE_DRIVER_VALUE ? null : draftDriverId,
          },
          taskId: task.id,
        },
        { onSuccess: onTaskUpdated },
      );
      return;
    }
    if (mode === TASK_ASSIGNMENT_MODE.SELF_ASSIGN) {
      const isReleasing = user !== null && draftDriverId === NONE_DRIVER_VALUE && persisted.driverId === user.id;
      if (isReleasing) {
        releaseMutation.mutate(task.id, { onSuccess: onTaskUpdated });
        return;
      }
      selfAssignMutation.mutate(task.id, { onSuccess: onTaskUpdated });
    }
  }

  const draft: DraftAssignment = { carrierId: draftCarrierId, driverId: draftDriverId };
  const canSave = resolveCanSave(mode, draft, persisted, user ?? null);
  const isDirty = resolveIsDirty(mode, draft, persisted, user ?? null);

  return {
    canSave,
    draftCarrierId,
    draftDriverId,
    handleCancel,
    handleCarrierChange,
    handleDriverChange: setDraftDriverId,
    handleSave,
    isDirty,
    isPending,
  };
}
